import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Loading, InlineNotification } from '@carbon/react';

// Import themes
// import { sigma08CarbonTheme } from './theme/carbonTheme';
import CarbonThemeProvider from './components/providers/CarbonThemeProvider';
import { ThemeProvider } from './contexts/ThemeContext';

// Import error boundary
import ErrorBoundary from './components/ErrorBoundary';

// Import performance monitoring
import WebVitalsMonitor from './components/performance/WebVitalsMonitor';
import { preloadImages, preconnectDomains } from './components/performance/LazyLoad';
import { initWebVitals } from './services/webVitals';

// Import layouts
import ParentDashboardLayout from './layouts/ParentDashboardLayout';
import DashboardLayout from './components/layout/DashboardLayout';

// Import pages
import LoginPage from './pages/auth/LoginPage';
import RegistrationPage from './pages/auth/RegistrationPage';
import OAuthCallbackPage from './pages/auth/OAuthCallbackPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import VerifyEmailPage from './pages/auth/VerifyEmailPage';
// import AccountTypeSelector from './components/auth/AccountTypeSelector';
// import ParentDashboard from './pages/parent/ParentDashboard';
import ParentDashboardEnhanced from './pages/parent/ParentDashboardEnhanced';
import ParentDashboardFixed from './pages/parent/ParentDashboardFixed';
import AdminDashboard from './pages/admin/AdminDashboard';
import BackupDashboard from './pages/admin/BackupDashboard';
import TeacherDashboard from './pages/teacher/TeacherDashboard';
import TeacherContentManagement from './pages/teacher/TeacherContentManagement';
import TeacherAssignmentBuilder from './pages/teacher/TeacherAssignmentBuilder';
import TeacherGradeBook from './pages/teacher/TeacherGradeBook';
import TeacherClassManagement from './pages/teacher/TeacherClassManagement';
import ContentLibrary from './pages/teacher/ContentLibrary';
import CommunicationCenter from './pages/teacher/CommunicationCenter';
import ResourceSharing from './pages/teacher/ResourceSharing';
// import StudentDashboard from './pages/student/StudentDashboard';
// import StudentDashboardFixed from './pages/student/StudentDashboardFixed';
import StudentDashboardCarbon from './components/student/StudentDashboardCarbon';
import StudentDashboardMinimal from './pages/student/StudentDashboardMinimal';
// import TutorDashboard from './pages/tutor/TutorDashboard';
// import SupportDashboard from './pages/support/SupportDashboard';
import ContentManagementAdmin from './components/admin/ContentManagementAdmin';
import MarketingAnalyticsDashboard from './components/admin/MarketingAnalyticsDashboard';
import ABTestingDashboard from './components/admin/ABTestingDashboard';
import ABTestConfiguration from './components/admin/ABTestConfiguration';
import PlatformMonitoringDashboard from './components/admin/PlatformMonitoringDashboard';
import CreatorDashboard from './components/creator/CreatorDashboard';
import CreatorEditor from './components/creator/CreatorEditor';
import StudentProfile from './pages/student/StudentProfile';
import TutoringSession from './pages/tutoring/TutoringSession';
import AiTutoringChat from './pages/tutoring/AiTutoringChat';
import AITutorPage from './pages/student/AITutorPage';
import ProgressTracker from './pages/progress/ProgressTracker';
import FamilySettings from './pages/family/FamilySettings';
import { StorageManager } from './pages/storage/StorageManager';
import CacheManagementInterface from './components/admin/CacheManagementInterface';
import StudentBulkCreation from './pages/school/StudentBulkCreation';
import SchoolBilling from './pages/billing/SchoolBilling';
import IndividualBilling from './pages/billing/IndividualBilling';
import EnterpriseBilling from './pages/billing/EnterpriseBilling';
import FamilyBilling from './pages/billing/FamilyBilling';
import BillingDashboard from './pages/admin/BillingDashboard';

// Import storage-related pages
import MediaLibrary from './pages/MediaLibrary';
import PortfolioItems from './pages/PortfolioItems';
import CourseContent from './pages/CourseContent';
import AssignmentSubmit from './pages/AssignmentSubmit';
import PublicPortfolio from './pages/PublicPortfolio';
import PortfolioDiscovery from './pages/PortfolioDiscovery';

// Import collaboration pages
import ClassroomCollaborate from './pages/classroom/ClassroomCollaborate';
import GroupAssignment from './pages/assignments/GroupAssignment';
import FamilyDashboardCollaboration from './pages/family/FamilyDashboardCollaboration';
import TeamWorkspace from './pages/enterprise/TeamWorkspace';
import EnterpriseSettings from './pages/enterprise/EnterpriseSettings';

// Import enterprise dashboard
import EnterpriseDataDashboard from './components/admin/EnterpriseDataDashboard';

// Import data routing dashboard
import DataRoutingDashboard from './components/admin/DataRoutingDashboard';

// Import admin user management
import AdminUserManagementPage from './pages/admin/AdminUserManagementPage';
import RBACDashboard from './pages/admin/RBACDashboard';
import BulkRoleOperations from './pages/admin/BulkRoleOperations';
import RoleHierarchyPage from './pages/admin/RoleHierarchyPage';
import AuditLogDashboard from './pages/admin/AuditLogDashboard';
import RoleAssignmentPage from './pages/admin/RoleAssignmentPage';
import ComplianceReportsPage from './pages/admin/ComplianceReportsPage';
import Consistency from './pages/admin/Consistency';

// Import permission change notifications
import PermissionChangeNotifications from './components/rbac/PermissionChangeNotifications';

// Import marketing router
import MarketingRouter from './routes/MarketingRouter';

// Import individual learning pages
import IndividualLearningDashboard from './pages/individual/IndividualLearningDashboard';
import IndividualLearningSession from './pages/individual/IndividualLearningSession';
import AchievementGallery from './components/individual/AchievementGallery';

// Import privacy and compliance pages
import { PrivacyDashboardPage } from './pages/privacy/PrivacyDashboardPage';
import { ConsentPage } from './pages/privacy/ConsentPage';
import { AuditLogPage } from './pages/privacy/AuditLogPage';
import { ParentConsentPage } from './pages/privacy/ParentConsentPage';
import { ComplianceDashboardPage } from './pages/admin/ComplianceDashboardPage';

// Import profile and learning pages
import PreferencesPage from './pages/profile/PreferencesPage';
import ProfessionalProfile from './pages/profile/ProfessionalProfile';
import ProfileSettingsPage from './pages/settings/ProfileSettingsPage';
import MyPermissionsPage from './pages/settings/MyPermissionsPage';
import SessionManagementPage from './pages/security/SessionManagementPage';
import SecuritySettingsPage from './pages/security/SecuritySettingsPage';
import LearningContinuePage from './pages/learning/LearningContinuePage';
import LearningDashboard from './pages/learning/LearningDashboard';
import CoursesPage from './pages/courses/CoursesPage';

// Import privacy components for other uses
import CookieConsent from './components/privacy/CookieConsent';
import PrivacyAnalyticsTracker from './components/analytics/PrivacyAnalyticsTracker';

// Import stores and services
import { useAuthStore, type User } from './store/authStore';
import { authService } from './services/authService';

// Content is already imported from @carbon/react, no need for this line

function App() {
  const { user, isLoading, error, setUser, setLoading, setError } = useAuthStore();

  // Privacy consent state management
  const [consentPreferences, setConsentPreferences] = React.useState({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  // Handle consent preference changes
  const handleConsentChange = React.useCallback((preferences: any) => {
    setConsentPreferences({
      necessary: preferences.necessary,
      analytics: preferences.analytics,
      marketing: preferences.marketing,
    });
  }, []);

  // Initialize performance monitoring
  React.useEffect(() => {
    // Initialize Web Vitals tracking
    initWebVitals();

    // Preconnect to critical domains (Google Fonts removed - using local fonts)
    preconnectDomains(['https://api.sigma08.com', '/api']);

    // Preload critical images
    preloadImages(['/logo.svg', '/images/hero-bg.jpg']);

    // Register service worker for offline capability (production and staging)
    if ('serviceWorker' in navigator && (import.meta.env.PROD || window.location.hostname.includes('staging'))) {
      navigator.serviceWorker.register('/service-worker.js').catch(error => {
        console.error('[Service Worker] Registration failed:', error);
      });
    }
  }, []);

  // Route validation for troubleshooting auth issues
  React.useEffect(() => {
    if (!user && !isLoading && error) {
      console.error('Authentication error:', error);
    }
  }, [user, isLoading, error]);

  // Check for existing authentication on app load
  useEffect(() => {
    // Check if we're on a marketing page - skip auth completely for these
    const pathname = window.location.pathname;
    const isMarketingPage =
      [
        '/',
        '/features',
        '/pricing',
        '/calculators',
        '/educators',
        '/parents',
        '/administrators',
        '/demo',
        '/trial',
        '/about',
        '/contact',
        '/value',
        '/success-stories',
      ].includes(pathname) ||
      pathname.startsWith('/content') ||
      pathname.startsWith('/articles') ||
      pathname.startsWith('/guides') ||
      pathname.startsWith('/research') ||
      pathname.startsWith('/marketing/') ||
      pathname.startsWith('/institutions/') ||
      pathname.startsWith('/solutions/') ||
      pathname === '/teachers' ||
      pathname === '/schools' ||
      pathname === '/districts' ||
      pathname === '/families';
    const isAuthPage =
      window.location.pathname.startsWith('/auth/') ||
      window.location.pathname === '/login' ||
      window.location.pathname.startsWith('/register');

    // BUGFIX: Failsafe timeout to prevent infinite loading state
    // If initAuth hangs or fails to complete, force loading to false after 10 seconds
    const failsafeTimeout = setTimeout(() => {
      console.warn('[App] Failsafe timeout triggered - forcing loading state to false');
      setLoading(false);
    }, 10000);

    const initAuth = async () => {
      try {
        // For marketing pages, skip authentication entirely
        if (isMarketingPage) {
          setUser(null);
          setError(null);
          setLoading(false);
          return;
        }

        setLoading(true);
        setError(null); // Clear any previous errors

        // Check both sessionStorage (temporary) and localStorage (remember me) for tokens
        // Also check sigma08_jwt_token for compatibility
        const sessionToken = sessionStorage.getItem('access_token') || sessionStorage.getItem('sigma08_jwt_token');
        const localToken = localStorage.getItem('access_token') || localStorage.getItem('sigma08_jwt_token');

        // Also check cookies as fallback
        let cookieToken = null;
        const cookies = document.cookie.split(';');
        for (const cookie of cookies) {
          const [name, value] = cookie.trim().split('=');
          if (name === 'auth_token' && value) {
            cookieToken = decodeURIComponent(value);
            break;
          }
        }

        const token = sessionToken || localToken || cookieToken;

        if (token) {
          // EPIC86 FIX: Check if persisted user is missing account_type and force refresh
          // This handles edge cases where migration didn't trigger or user was cached before backend fix
          const persistedUser = user;
          const needsRefresh = !persistedUser || !persistedUser.account_type;

          if (needsRefresh) {
            console.log('[App Init] Fetching fresh profile (account_type missing or no user)');
          }

          // Verify token and get user profile with timeout
          try {
            // Add timeout to prevent hanging on network issues
            // BUGFIX: Increased timeout from 5s to 15s for profile endpoint with multiple DB queries
            // Profile endpoint may need to query family_accounts, student_profiles, and user_auth tables
            const profilePromise = authService.getProfile();
            const timeoutPromise = new Promise((_, reject) =>
              setTimeout(() => reject(new Error('Profile fetch timeout')), 15000)
            );

            const userProfile = (await Promise.race([profilePromise, timeoutPromise])) as User;
            console.log('[App Init] Profile fetched, account_type:', userProfile.account_type);
            setUser(userProfile);
            setLoading(false); // BUGFIX: Set loading to false immediately after successful profile fetch
          } catch (profileError: any) {
            // Handle profile fetch error gracefully
            console.warn('Profile fetch failed:', profileError.message);
            // Only clear tokens if they're invalid (401) - don't clear on timeout
            // Timeout might be due to slow network, not invalid credentials
            if (profileError.response?.status === 401) {
              sessionStorage.removeItem('access_token');
              sessionStorage.removeItem('refresh_token');
              sessionStorage.removeItem('sigma08_jwt_token');
              localStorage.removeItem('access_token');
              localStorage.removeItem('refresh_token');
              localStorage.removeItem('sigma08_jwt_token');
              setUser(null);
              setLoading(false); // BUGFIX: Set loading to false on auth error
              // Don't set global error for auth pages - let LoginForm handle inline errors
              // Only set error for protected dashboard pages where user needs to know why they can't access
              if (!isAuthPage) {
                setError('Authentication session expired. Please login again.');
              }
            } else if (profileError.message?.includes('timeout')) {
              // On timeout, keep tokens but set error to allow retry
              setUser(null);
              setLoading(false); // BUGFIX: Set loading to false on timeout
              if (!isAuthPage) {
                setError('Profile fetch timeout. Please refresh the page to try again.');
              }
            } else {
              // Other errors - keep tokens but clear user
              setUser(null);
              setLoading(false); // BUGFIX: Set loading to false on other errors
              if (!isAuthPage) {
                setError(profileError.message || 'Failed to load profile');
              }
            }
          }
        } else {
          // No token, ensure user is cleared
          setUser(null);
          // IMPORTANT: Set loading to false when no token is found
          setLoading(false);
        }
      } catch (error: any) {
        console.error('Authentication initialization failed:', error);
        // Clear invalid tokens from both storages
        sessionStorage.removeItem('access_token');
        sessionStorage.removeItem('refresh_token');
        sessionStorage.removeItem('sigma08_jwt_token');
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('sigma08_jwt_token');
        setUser(null);
        // Don't set global error for auth pages - let LoginForm handle inline errors
        // Don't show error for auth failures on marketing pages either
        if (!isAuthPage && !isMarketingPage) {
          setError(error.message || 'Authentication failed');
        }
      } finally {
        // Clear the failsafe timeout since initAuth completed
        clearTimeout(failsafeTimeout);

        // Reset loading state appropriately
        if (isMarketingPage) {
          // Marketing pages already handled above
        } else if (isAuthPage) {
          // Auth pages need immediate loading disable to prevent form conflicts
          setLoading(false);
        } else {
          // Dashboard pages can have a small delay for smoother UX
          setTimeout(() => setLoading(false), 100);
        }
      }
    };

    // Start initialization
    initAuth();

    // BUGFIX: Return cleanup function to clear timeout if component unmounts
    return () => {
      clearTimeout(failsafeTimeout);
    };
  }, []); // Empty dependency array - run only once on mount

  // Show loading spinner during initial authentication check - but NOT for auth pages
  const isAuthPage =
    window.location.pathname.startsWith('/auth/') ||
    window.location.pathname === '/login' ||
    window.location.pathname.startsWith('/register');

  if (isLoading && !isAuthPage) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <Loading withOverlay={false} />
          <div
            style={{
              marginTop: 'var(--cds-spacing-05)',
              color: 'var(--cds-interactive-01)',
              fontSize: 'var(--cds-body-compact-01-font-size)',
              fontWeight: 500,
            }}
          >
            Loading Sigma08 Learning Platform...
          </div>
          {/* EPIC86 DEBUG: Show user state to diagnose routing issues */}
          <div style={{ marginTop: '1rem', fontSize: '12px', color: '#666', fontFamily: 'monospace' }}>
            <div>DEBUG - isLoading: {isLoading.toString()}</div>
            <div>DEBUG - user: {user ? 'exists' : 'null'}</div>
            <div>DEBUG - user.account_type: {user?.account_type || 'undefined'}</div>
            <div>DEBUG - user.role: {user?.role || 'undefined'}</div>
            <div>DEBUG - pathname: {window.location.pathname}</div>
          </div>
        </div>
      </div>
    );
  }

  // Show error if authentication failed - but only for NON-auth pages (dashboard, etc.)
  // For auth pages (login/register), let the form components handle error display inline
  // For marketing pages, show them even with auth errors
  if (error && !user && !isAuthPage) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ maxWidth: 400, padding: 24 }}>
          <InlineNotification title="Authentication Error" subtitle={error} kind="error" style={{ marginBottom: 16 }} />
          <LoginPage />
        </div>
      </div>
    );
  }

  // If not authenticated, show marketing pages or login
  if (!user) {
    return (
      <ErrorBoundary>
        <ThemeProvider>
          <CarbonThemeProvider>
            <div style={{ minHeight: '100vh' }}>
              {/* Real-time permission change notifications */}
              <PermissionChangeNotifications />

              <Routes>
                {/* Auth routes - must come before catch-all */}
                <Route path="/auth/login" element={<LoginPage />} />
                <Route path="/auth/register" element={<RegistrationPage />} />
                <Route path="/auth/oauth-callback" element={<OAuthCallbackPage />} />
                <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
                <Route path="/auth/verify-email" element={<VerifyEmailPage />} />

                {/* Registration flow routes - must come before catch-all */}
                <Route path="/register/select-type" element={<RegistrationPage />} />
                <Route path="/register/individual" element={<RegistrationPage accountType="individual" />} />
                <Route path="/register/family" element={<RegistrationPage accountType="family" />} />
                <Route path="/register/school/:schoolId/students" element={<StudentBulkCreation />} />
                <Route path="/register/corporate" element={<RegistrationPage accountType="corporate" />} />
                <Route path="/register/enterprise" element={<RegistrationPage accountType="corporate" />} />
                <Route path="/register/school" element={<RegistrationPage accountType="school" />} />

                {/* Role-based registration routes */}
                <Route path="/auth/register/teacher" element={<RegistrationPage accountType="individual" />} />
                <Route path="/auth/register/student" element={<RegistrationPage accountType="individual" />} />
                <Route path="/auth/register/parent" element={<RegistrationPage accountType="family" />} />

                {/* Public portfolio routes */}
                <Route path="/public/portfolios/:user_id" element={<PublicPortfolio />} />
                <Route path="/portfolios/discover" element={<PortfolioDiscovery />} />
                <Route path="/portfolio/:username" element={<PublicPortfolio />} />

                {/* Protected routes - redirect to login if accessed without authentication */}
                <Route path="/teacher" element={<Navigate to="/auth/login" replace />} />
                <Route path="/teacher/*" element={<Navigate to="/auth/login" replace />} />
                <Route path="/admin" element={<Navigate to="/auth/login" replace />} />
                <Route path="/admin/*" element={<Navigate to="/auth/login" replace />} />
                <Route path="/student" element={<Navigate to="/auth/login" replace />} />
                <Route path="/student/*" element={<Navigate to="/auth/login" replace />} />
                <Route path="/parent" element={<Navigate to="/auth/login" replace />} />
                <Route path="/parent/*" element={<Navigate to="/auth/login" replace />} />
                <Route path="/dashboard" element={<Navigate to="/auth/login" replace />} />
                <Route path="/dashboard/*" element={<Navigate to="/auth/login" replace />} />
                <Route path="/settings" element={<Navigate to="/auth/login" replace />} />
                <Route path="/settings/*" element={<Navigate to="/auth/login" replace />} />
                <Route path="/security" element={<Navigate to="/auth/login" replace />} />
                <Route path="/security/*" element={<Navigate to="/auth/login" replace />} />
                <Route path="/profile" element={<Navigate to="/auth/login" replace />} />
                <Route path="/profile/*" element={<Navigate to="/auth/login" replace />} />

                {/* Legacy routes for backwards compatibility */}
                <Route path="/login" element={<Navigate to="/auth/login" replace />} />
                <Route path="/register" element={<Navigate to="/register/select-type" replace />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />

                {/* Privacy routes for staging testing - accessible without authentication */}
                <Route path="/privacy/dashboard" element={<PrivacyDashboardPage />} />
                <Route path="/privacy/consent" element={<ConsentPage />} />
                <Route path="/privacy/audit" element={<AuditLogPage />} />
                <Route path="/parent/child-consent/:childId" element={<ParentConsentPage />} />
                <Route path="/admin/compliance" element={<ComplianceDashboardPage />} />

                {/* Marketing routes handled by MarketingRouter - catch-all must be last */}
                <Route path="/*" element={<MarketingRouter />} />
              </Routes>

              {/* Privacy-compliant analytics for marketing pages */}
              <PrivacyAnalyticsTracker
                enabled={true}
                endpoint="/api/v1/analytics/events"
                debugMode={!import.meta.env.PROD}
                consentStatus={consentPreferences}
              />

              {/* Cookie consent banner for marketing pages */}
              <CookieConsent privacyPolicyVersion="1.0.0" onConsentChange={handleConsentChange} />
            </div>
          </CarbonThemeProvider>
        </ThemeProvider>
      </ErrorBoundary>
    );
  }

  // Epic17: Handle missing/undefined role - prevent infinite loading
  if (user && !user.role) {
    return (
      <ErrorBoundary>
        <ThemeProvider>
          <CarbonThemeProvider>
            <div
              style={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 'var(--cds-spacing-05)',
              }}
            >
              <div style={{ maxWidth: '600px', width: '100%' }}>
                <InlineNotification
                  kind="error"
                  title="Account Configuration Error"
                  subtitle="Your account is missing required role information. Please contact support or try logging out and back in."
                  lowContrast
                />
                <div style={{ marginTop: 'var(--cds-spacing-05)', display: 'flex', gap: 'var(--cds-spacing-03)' }}>
                  <button
                    className="cds--btn cds--btn--primary"
                    onClick={() => {
                      localStorage.removeItem('auth_token');
                      window.location.href = '/login';
                    }}
                  >
                    Return to Login
                  </button>
                  <button
                    className="cds--btn cds--btn--secondary"
                    onClick={() => {
                      window.location.reload();
                    }}
                  >
                    Retry
                  </button>
                </div>
              </div>
            </div>
          </CarbonThemeProvider>
        </ThemeProvider>
      </ErrorBoundary>
    );
  }

  // Authenticated user routes with theme configuration and error boundary
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <CarbonThemeProvider>
          <Routes>
            {/* Parent routes with new layout */}
            {user.role === 'parent' && (
              <Route path="/" element={<ParentDashboardLayout />}>
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="dashboard" element={<ParentDashboardFixed />} />
                <Route path="children" element={<StudentProfile />} />
                <Route path="students/:studentId" element={<StudentProfile />} />
                <Route path="students/:studentId/progress" element={<ProgressTracker />} />
                <Route path="progress" element={<ProgressTracker />} />
                <Route path="insights" element={<ParentDashboardEnhanced />} />
                <Route path="billing" element={<FamilyBilling />} />
                <Route path="billing/family" element={<FamilyBilling />} />
                <Route path="family/dashboard" element={<FamilyDashboardCollaboration />} />
                <Route path="family/settings" element={<FamilySettings />} />
                <Route path="settings/profile" element={<ProfileSettingsPage />} />
                <Route path="settings/my-permissions" element={<MyPermissionsPage />} />
                <Route path="settings/security" element={<SecuritySettingsPage />} />
                <Route path="settings/security/sessions" element={<SessionManagementPage />} />
                <Route path="security" element={<SecuritySettingsPage />} />
                <Route path="security/sessions" element={<SessionManagementPage />} />
                <Route path="storage" element={<StorageManager />} />
                <Route path="privacy/dashboard" element={<PrivacyDashboardPage />} />
                <Route path="privacy/consent" element={<ConsentPage />} />
                <Route path="privacy/audit" element={<AuditLogPage />} />
                <Route path="parent/child-consent/:childId" element={<ParentConsentPage />} />
                <Route path="privacy" element={<Navigate to="/privacy/dashboard" replace />} />
                <Route path="profile/preferences" element={<PreferencesPage />} />
                <Route path="learning/continue" element={<LearningContinuePage />} />
              </Route>
            )}

            {/* Individual Adult Learner routes - CHECK FIRST before student routes */}
            {user.account_type === 'individual' && (
              <Route path="/" element={<DashboardLayout />}>
                <Route path="dashboard" element={<IndividualLearningDashboard />} />
                <Route path="individual-learning" element={<IndividualLearningDashboard />} />
                <Route path="individual-learning/session" element={<IndividualLearningSession />} />
                <Route path="individual-learning/session/:sessionId" element={<IndividualLearningSession />} />
                <Route path="learning/dashboard" element={<IndividualLearningDashboard />} />
                <Route path="learning/session" element={<IndividualLearningSession />} />
                <Route path="courses" element={<CoursesPage />} />
                <Route path="pathways" element={<IndividualLearningDashboard />} />
                <Route path="progress" element={<ProgressTracker />} />
                <Route path="achievements" element={<AchievementGallery learnerId={user?.user_id || ''} />} />
                <Route path="profile" element={<StudentProfile />} />
                <Route path="profile/preferences" element={<PreferencesPage />} />
                <Route path="billing" element={<IndividualBilling />} />
                <Route path="billing/individual" element={<IndividualBilling />} />
                <Route path="settings/profile" element={<ProfileSettingsPage />} />
                <Route path="settings/my-permissions" element={<MyPermissionsPage />} />
                <Route path="settings/security" element={<SecuritySettingsPage />} />
                <Route path="settings/security/sessions" element={<SessionManagementPage />} />
                <Route path="security" element={<SecuritySettingsPage />} />
                <Route path="security/sessions" element={<SessionManagementPage />} />
                <Route path="learning/continue" element={<LearningContinuePage />} />
                <Route path="storage" element={<StorageManager />} />
                <Route path="privacy/dashboard" element={<PrivacyDashboardPage />} />
                <Route path="privacy/consent" element={<ConsentPage />} />
                <Route path="privacy/audit" element={<AuditLogPage />} />
                <Route path="privacy" element={<Navigate to="/privacy/dashboard" replace />} />
                <Route path="portfolio" element={<PortfolioItems />} />
                <Route path="portfolio/items" element={<PortfolioItems />} />
                <Route path="media/library" element={<MediaLibrary />} />
                <Route path="" element={<Navigate to="/dashboard" replace />} />
              </Route>
            )}

            {/* Enterprise User routes - Corporate team members */}
            {user.role === 'enterprise_user' && (
              <Route path="/" element={<DashboardLayout />}>
                <Route path="dashboard" element={<IndividualLearningDashboard />} />
                <Route path="learning/dashboard" element={<IndividualLearningDashboard />} />
                <Route path="learning/session" element={<IndividualLearningSession />} />
                <Route path="courses" element={<CoursesPage />} />
                <Route path="progress" element={<ProgressTracker />} />
                <Route path="profile" element={<StudentProfile />} />
                <Route path="profile/preferences" element={<PreferencesPage />} />
                <Route path="billing" element={<EnterpriseBilling />} />
                <Route path="billing/enterprise" element={<EnterpriseBilling />} />
                <Route path="enterprise/team-workspace" element={<TeamWorkspace />} />
                <Route path="enterprise/team-workspace/:projectId" element={<TeamWorkspace />} />
                <Route path="enterprise/settings" element={<EnterpriseSettings />} />
                <Route path="settings/profile" element={<ProfileSettingsPage />} />
                <Route path="settings/my-permissions" element={<MyPermissionsPage />} />
                <Route path="settings/security" element={<SecuritySettingsPage />} />
                <Route path="settings/security/sessions" element={<SessionManagementPage />} />
                <Route path="security" element={<SecuritySettingsPage />} />
                <Route path="security/sessions" element={<SessionManagementPage />} />
                <Route path="storage" element={<StorageManager />} />
                <Route path="privacy/dashboard" element={<PrivacyDashboardPage />} />
                <Route path="privacy/consent" element={<ConsentPage />} />
                <Route path="privacy/audit" element={<AuditLogPage />} />
                <Route path="privacy" element={<Navigate to="/privacy/dashboard" replace />} />
                <Route path="" element={<Navigate to="/dashboard" replace />} />
              </Route>
            )}

            {/* Student routes with existing layout - ONLY if NOT individual account type */}
            {user.role === 'student' && user.account_type !== 'individual' && (
              <Route path="/" element={<DashboardLayout />}>
                <Route path="student" element={<StudentDashboardCarbon />} />
                <Route path="dashboard" element={<StudentDashboardCarbon />} />
                <Route path="profile" element={<StudentProfile />} />
                <Route path="settings/profile" element={<ProfileSettingsPage />} />
                <Route path="settings/my-permissions" element={<MyPermissionsPage />} />
                <Route path="settings/security" element={<SecuritySettingsPage />} />
                <Route path="settings/security/sessions" element={<SessionManagementPage />} />
                <Route path="security" element={<SecuritySettingsPage />} />
                <Route path="security/sessions" element={<SessionManagementPage />} />
                <Route path="student/ai-tutor" element={<AITutorPage />} />
                <Route path="tutoring" element={<AiTutoringChat />} />
                <Route path="tutoring/session" element={<TutoringSession />} />
                <Route path="assignments/group/:id" element={<GroupAssignment />} />
                <Route path="assignments/submit" element={<AssignmentSubmit />} />
                <Route path="classroom/collaborate" element={<ClassroomCollaborate />} />
                <Route path="classroom/collaborate/:sessionId" element={<ClassroomCollaborate />} />
                <Route path="progress" element={<ProgressTracker />} />
                <Route path="media/library" element={<MediaLibrary />} />
                <Route path="portfolio/items" element={<PortfolioItems />} />
                <Route path="courses/content/:id" element={<CourseContent />} />
                <Route path="storage" element={<StorageManager />} />
                <Route path="privacy/dashboard" element={<PrivacyDashboardPage />} />
                <Route path="privacy/consent" element={<ConsentPage />} />
                <Route path="privacy/audit" element={<AuditLogPage />} />
                <Route path="privacy" element={<Navigate to="/privacy/dashboard" replace />} />
                <Route path="profile/preferences" element={<PreferencesPage />} />
                <Route path="learning/continue" element={<LearningContinuePage />} />
                <Route path="" element={<Navigate to="/student" replace />} />
              </Route>
            )}

            {/* Admin routes with proper Admin Dashboard */}
            {user.role === 'platform_admin' && (
              <Route path="/" element={<DashboardLayout />}>
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="student-test" element={<StudentDashboardMinimal />} />
                <Route path="admin" element={<AdminDashboard />} />
                <Route path="admin/users" element={<AdminUserManagementPage />} />
                <Route path="admin/rbac" element={<RBACDashboard />} />
                <Route path="admin/rbac-dashboard" element={<RBACDashboard />} />
                <Route path="admin/rbac/hierarchy" element={<RoleHierarchyPage />} />
                <Route path="admin/rbac/assign-role" element={<RoleAssignmentPage />} />
                <Route path="admin/rbac/bulk-operations" element={<BulkRoleOperations />} />
                <Route path="admin/rbac/audit" element={<AuditLogDashboard />} />
                <Route path="admin/rbac/compliance" element={<ComplianceReportsPage />} />
                <Route path="admin/consistency" element={<Consistency />} />
                <Route path="admin/data-management" element={<AdminDashboard />} />
                <Route path="admin/data-routing" element={<DataRoutingDashboard />} />
                <Route path="admin/platform-monitoring" element={<PlatformMonitoringDashboard />} />
                <Route path="admin/marketing" element={<ContentManagementAdmin integrationMode="standalone" />} />
                <Route path="admin/marketing/analytics" element={<MarketingAnalyticsDashboard />} />
                <Route path="admin/ab-testing" element={<ABTestingDashboard />} />
                <Route
                  path="admin/ab-testing/create"
                  element={<ABTestConfiguration onSave={() => {}} onCancel={() => {}} />}
                />
                <Route
                  path="admin/ab-testing/:testId/edit"
                  element={<ABTestConfiguration onSave={() => {}} onCancel={() => {}} />}
                />
                <Route path="admin/content-management" element={<Navigate to="/admin/marketing" replace />} />
                <Route
                  path="admin/cache-management"
                  element={<CacheManagementInterface onCacheInvalidate={() => {}} onPerformanceAlert={() => {}} />}
                />
                <Route path="admin/backup" element={<BackupDashboard />} />
                <Route path="admin/billing" element={<BillingDashboard />} />
                <Route path="admin/compliance" element={<ComplianceDashboardPage />} />
                <Route path="admin/storage" element={<StorageManager />} />
                <Route path="privacy/dashboard" element={<PrivacyDashboardPage />} />
                <Route path="privacy/consent" element={<ConsentPage />} />
                <Route path="privacy/audit" element={<AuditLogPage />} />
                <Route path="parent/child-consent/:childId" element={<ParentConsentPage />} />
                <Route path="privacy" element={<Navigate to="/privacy/dashboard" replace />} />
                <Route path="media/library" element={<MediaLibrary />} />
                <Route path="portfolio/items" element={<PortfolioItems />} />
                <Route path="courses/content/:id" element={<CourseContent />} />
                <Route path="assignments/submit" element={<AssignmentSubmit />} />
                <Route path="enterprise/team-workspace" element={<TeamWorkspace />} />
                <Route path="enterprise/team-workspace/:projectId" element={<TeamWorkspace />} />
                <Route path="team/workspace" element={<Navigate to="/enterprise/team-workspace" replace />} />
                <Route
                  path="team/workspace/:projectId"
                  element={<Navigate to="/enterprise/team-workspace/:projectId" replace />}
                />
                <Route path="enterprise/settings" element={<EnterpriseSettings />} />
                <Route path="classroom/collaborate" element={<ClassroomCollaborate />} />
                <Route path="classroom/collaborate/:sessionId" element={<ClassroomCollaborate />} />
                <Route path="classroom/live/:classId" element={<ClassroomCollaborate />} />
                <Route path="family/dashboard" element={<FamilyDashboardCollaboration />} />
                <Route path="assignments/group/:id" element={<GroupAssignment />} />
                <Route path="users" element={<AdminDashboard />} />
                <Route path="storage" element={<StorageManager />} />
                <Route path="settings" element={<FamilySettings />} />
                <Route path="profile/preferences" element={<PreferencesPage />} />
                <Route path="learning/continue" element={<LearningContinuePage />} />
                <Route path="" element={<Navigate to="/dashboard" replace />} />
              </Route>
            )}

            {/* District Admin routes with Enterprise Dashboard */}
            {user.role === 'district_admin' && (
              <Route path="/" element={<DashboardLayout />}>
                <Route path="dashboard" element={<EnterpriseDataDashboard />} />
                <Route path="district" element={<EnterpriseDataDashboard />} />
                <Route path="enterprise" element={<EnterpriseDataDashboard />} />
                <Route path="enterprise/data-management" element={<EnterpriseDataDashboard />} />
                <Route path="enterprise/analytics" element={<MarketingAnalyticsDashboard />} />
                <Route path="enterprise/schools" element={<EnterpriseDataDashboard />} />
                <Route path="enterprise/performance" element={<EnterpriseDataDashboard />} />
                <Route path="enterprise/compliance" element={<ComplianceDashboardPage />} />
                <Route path="enterprise/team-workspace" element={<TeamWorkspace />} />
                <Route path="enterprise/team-workspace/:projectId" element={<TeamWorkspace />} />
                <Route path="enterprise/settings" element={<EnterpriseSettings />} />
                <Route path="admin/marketing" element={<ContentManagementAdmin integrationMode="standalone" />} />
                <Route path="admin/marketing/analytics" element={<MarketingAnalyticsDashboard />} />
                <Route path="admin/storage" element={<StorageManager />} />
                <Route path="privacy/dashboard" element={<PrivacyDashboardPage />} />
                <Route path="privacy/consent" element={<ConsentPage />} />
                <Route path="privacy/audit" element={<AuditLogPage />} />
                <Route path="classroom/collaborate" element={<ClassroomCollaborate />} />
                <Route path="classroom/collaborate/:sessionId" element={<ClassroomCollaborate />} />
                <Route path="storage" element={<StorageManager />} />
                <Route path="profile/preferences" element={<PreferencesPage />} />
                <Route path="" element={<Navigate to="/dashboard" replace />} />
              </Route>
            )}

            {/* Principal (School Admin) routes with Enterprise Dashboard */}
            {user.role === 'principal' && (
              <Route path="/" element={<DashboardLayout />}>
                <Route path="dashboard" element={<EnterpriseDataDashboard />} />
                <Route path="school" element={<EnterpriseDataDashboard />} />
                <Route path="enterprise" element={<EnterpriseDataDashboard />} />
                <Route path="enterprise/data-management" element={<EnterpriseDataDashboard />} />
                <Route path="enterprise/analytics" element={<MarketingAnalyticsDashboard />} />
                <Route path="enterprise/teachers" element={<EnterpriseDataDashboard />} />
                <Route path="enterprise/students" element={<EnterpriseDataDashboard />} />
                <Route path="enterprise/performance" element={<EnterpriseDataDashboard />} />
                <Route path="enterprise/compliance" element={<ComplianceDashboardPage />} />
                <Route path="enterprise/team-workspace" element={<TeamWorkspace />} />
                <Route path="enterprise/team-workspace/:projectId" element={<TeamWorkspace />} />
                <Route path="enterprise/settings" element={<EnterpriseSettings />} />
                <Route path="register/school/:schoolId/students" element={<StudentBulkCreation />} />
                <Route path="admin/marketing" element={<ContentManagementAdmin integrationMode="standalone" />} />
                <Route path="admin/storage" element={<StorageManager />} />
                <Route path="privacy/dashboard" element={<PrivacyDashboardPage />} />
                <Route path="privacy/consent" element={<ConsentPage />} />
                <Route path="privacy/audit" element={<AuditLogPage />} />
                <Route path="classroom/collaborate" element={<ClassroomCollaborate />} />
                <Route path="classroom/collaborate/:sessionId" element={<ClassroomCollaborate />} />
                <Route path="storage" element={<StorageManager />} />
                <Route path="profile/preferences" element={<PreferencesPage />} />
                <Route path="" element={<Navigate to="/dashboard" replace />} />
              </Route>
            )}

            {/* School Admin routes with proper School Admin Dashboard */}
            {(user.role === 'school_admin' || user.role === 'district_admin') && (
              <Route path="/" element={<DashboardLayout />}>
                <Route index element={<Navigate to="/school/billing" replace />} />
                <Route path="dashboard" element={<Navigate to="/school/billing" replace />} />
                <Route path="school/billing" element={<SchoolBilling />} />
                <Route path="school/students" element={<StudentBulkCreation />} />
                <Route path="settings/profile" element={<ProfileSettingsPage />} />
                <Route path="settings/my-permissions" element={<MyPermissionsPage />} />
                <Route path="settings/security" element={<SecuritySettingsPage />} />
                <Route path="settings/security/sessions" element={<SessionManagementPage />} />
              </Route>
            )}

            {/* Teacher routes with proper Teacher Dashboard */}
            {user.role === 'teacher' && (
              <Route path="/" element={<DashboardLayout />}>
                <Route index element={<Navigate to="/teacher" replace />} />
                <Route path="dashboard" element={<Navigate to="/teacher" replace />} />
                <Route path="teacher" element={<TeacherDashboard />} />
                <Route path="teacher/dashboard" element={<TeacherDashboard />} />
                <Route path="teacher/content" element={<TeacherContentManagement />} />
                <Route path="teacher/content-library" element={<ContentLibrary />} />
                <Route path="teacher/communications" element={<CommunicationCenter />} />
                <Route path="teacher/resources" element={<ResourceSharing />} />
                <Route path="teacher/classes" element={<TeacherClassManagement />} />
                <Route path="teacher/students" element={<StudentProfile />} />
                <Route path="teacher/progress" element={<ProgressTracker />} />
                <Route path="teacher/assignments" element={<TeacherAssignmentBuilder />} />
                <Route path="teacher/gradebook" element={<TeacherGradeBook />} />
                <Route path="content" element={<TeacherContentManagement />} />
                <Route path="content-library" element={<ContentLibrary />} />
                <Route path="communications" element={<CommunicationCenter />} />
                <Route path="resources" element={<ResourceSharing />} />
                <Route path="classes" element={<TeacherClassManagement />} />
                <Route path="students" element={<StudentProfile />} />
                <Route path="progress" element={<ProgressTracker />} />
                <Route path="assignments" element={<TeacherAssignmentBuilder />} />
                <Route path="assignments/submit" element={<AssignmentSubmit />} />
                <Route path="assignments/group/:id" element={<GroupAssignment />} />
                <Route path="classroom/collaborate" element={<ClassroomCollaborate />} />
                <Route path="classroom/collaborate/:sessionId" element={<ClassroomCollaborate />} />
                <Route path="classroom/live/:classId" element={<ClassroomCollaborate />} />
                <Route path="gradebook" element={<TeacherGradeBook />} />
                <Route path="settings/profile" element={<ProfileSettingsPage />} />
                <Route path="settings/my-permissions" element={<MyPermissionsPage />} />
                <Route path="settings/security" element={<SecuritySettingsPage />} />
                <Route path="settings/security/sessions" element={<SessionManagementPage />} />
                <Route path="security" element={<SecuritySettingsPage />} />
                <Route path="security/sessions" element={<SessionManagementPage />} />
                <Route path="media/library" element={<MediaLibrary />} />
                <Route path="portfolio/items" element={<PortfolioItems />} />
                <Route path="courses/content/:id" element={<CourseContent />} />
                <Route path="storage" element={<StorageManager />} />
              </Route>
            )}

            {/* Creator routes with Creator Dashboard */}
            {user.role === 'creator' && (
              <Route path="/" element={<DashboardLayout />}>
                <Route path="dashboard" element={<CreatorDashboard />} />
                <Route path="creator" element={<CreatorDashboard />} />
                <Route path="creator/content" element={<Navigate to="/dashboard" replace />} />
                <Route path="creator/editor/new" element={<CreatorEditor mode="create" />} />
                <Route path="creator/editor/:contentId" element={<CreatorEditor mode="edit" />} />
                <Route path="creator/templates" element={<CreatorDashboard />} />
                <Route path="creator/analytics" element={<CreatorDashboard />} />
                <Route path="creator/settings" element={<FamilySettings />} />
                <Route path="" element={<Navigate to="/dashboard" replace />} />
              </Route>
            )}

            {/* TODO: Add tutor and support roles to User interface in authStore.ts */}
            {/* Tutor routes with Tutor Dashboard */}
            {/* {user.role === 'tutor' && (
          <Route path="/" element={<DashboardLayout />}>
            <Route path="dashboard" element={<TutorDashboard />} />
            <Route path="sessions" element={<TutorDashboard />} />
            <Route path="students" element={<TutorDashboard />} />
            <Route path="schedule" element={<TutorDashboard />} />
            <Route path="resources" element={<TutorDashboard />} />
            <Route path="" element={<Navigate to="/dashboard" replace />} />
          </Route>
        )} */}

            {/* Support routes with Support Dashboard */}
            {/* {user.role === 'support' && (
          <Route path="/" element={<DashboardLayout />}>
            <Route path="dashboard" element={<SupportDashboard />} />
            <Route path="tickets" element={<SupportDashboard />} />
            <Route path="knowledge" element={<SupportDashboard />} />
            <Route path="reports" element={<SupportDashboard />} />
            <Route path="" element={<Navigate to="/dashboard" replace />} />
          </Route>
        )} */}

            {/* Common authenticated routes available to all users with dashboard layout */}
            <Route path="/" element={<DashboardLayout />}>
              <Route path="settings/profile" element={<ProfileSettingsPage />} />
              <Route path="settings/my-permissions" element={<MyPermissionsPage />} />
              <Route path="settings/security" element={<SecuritySettingsPage />} />
              <Route path="settings/security/sessions" element={<SessionManagementPage />} />
              <Route path="security" element={<SecuritySettingsPage />} />
              <Route path="security/sessions" element={<SessionManagementPage />} />
              <Route path="profile/preferences" element={<PreferencesPage />} />
              <Route path="profile/professional" element={<ProfessionalProfile />} />
              <Route path="learning/continue" element={<LearningContinuePage />} />
              <Route path="dashboard/learning" element={<LearningDashboard />} />
              <Route path="portfolio" element={<PortfolioItems />} />
              <Route path="portfolio/items" element={<PortfolioItems />} />
              <Route path="portfolio/create" element={<PortfolioItems />} />
              <Route path="media/library" element={<MediaLibrary />} />
              <Route path="storage" element={<StorageManager />} />
            </Route>

            {/* Auth routes redirect to dashboard when logged in - must come after role-specific routes */}
            <Route path="/auth/*" element={<Navigate to="/dashboard" replace />} />
            <Route path="/login" element={<Navigate to="/dashboard" replace />} />

            {/* Catch all - redirect to dashboard */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>

          {/* Performance monitoring component */}
          <WebVitalsMonitor
            enabled={true}
            reportingEndpoint="/api/v1/analytics/vitals"
            sampleRate={1.0}
            debug={!import.meta.env.PROD}
          />

          {/* Privacy-compliant analytics tracker */}
          <PrivacyAnalyticsTracker
            enabled={true}
            endpoint="/api/v1/analytics/events"
            debugMode={!import.meta.env.PROD}
            consentStatus={consentPreferences}
            visitorId={user?.user_id}
          />

          {/* Cookie consent banner */}
          <CookieConsent privacyPolicyVersion="1.0.0" onConsentChange={handleConsentChange} />
        </CarbonThemeProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App; // Cache bust: 1758790550
